import { useEffect, useRef } from "react";
import { rasterTileRegistry } from "../../apis/RasterTileRegistry";
import { sharedPopupManager } from "../../utils/popup/SharedPopupManager";
import { toBackendDepartmentCode } from "../../utils/departmentCode";
import AuthAPI from "../../apis/auth/AuthAPI";

const authApi = new AuthAPI();

const useRasterTile = (
  mapRef,
  layerKey,
  opacity = 1,
  show = false,
  options = {},
) => {
  const isVisibleRef = useRef(show);
  const cleanupTimeoutRef = useRef(null);

  const config = rasterTileRegistry[layerKey];
  const isDynamic = config?.isDynamic;

  // Build the query params shared by both tile URLs and identify calls
  const buildQueryParams = (opts) => {
    const params = [];
    if (opts?.country) params.push(`country=${opts.country}`);
    if (opts?.date) params.push(`date=${opts.date}`);
    if (opts?.time) params.push(`time=${encodeURIComponent(opts.time)}`);
    if (opts?.utc || opts?.timezone)
      params.push(`timezone=${opts.utc || opts.timezone}`);

    // Handle department as array for multiple selection
    if (
      opts?.department &&
      Array.isArray(opts.department) &&
      opts.department.length > 0
    ) {
      // If 'tous' is selected or all departments are selected, don't filter by department
      const depts = opts.department.filter((d) => d !== "");
      if (depts.length > 0 && depts.length < 95) {
        depts.forEach((dept) =>
          params.push(`department=${toBackendDepartmentCode(dept)}`),
        );
      }
    } else if (
      opts?.department &&
      typeof opts.department === "string" &&
      opts.department !== ""
    ) {
      params.push(`department=${toBackendDepartmentCode(opts.department)}`);
    }

    return params;
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !config) return;

    const { sourceId, layerId } = config;
    isVisibleRef.current = show;

    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    if (config.popup?.enabled) {
      sharedPopupManager.init(map);
    }

    // 🔑 Validate/refresh auth exactly once per "show" transition (or per
    // dynamic-options change), right before this layer starts requesting
    // tiles. This effect only re-runs when `show`/`layerKey`/options change —
    // never on zoom/pan/bbox changes — so this fires once per toggle,
    // not once per tile. Token refresh (via x-access-token header) and
    // 401 logout are both handled automatically by the shared axios
    // interceptor that AuthAPI's underlying `api` instance uses.
    if (show) {
      authApi.checkAuth().catch(() => {
        // 401 handling already lives in the shared axios interceptor
      });
    }

    const applyLayer = () => {
      if (!show) return;

      // 1️⃣ Build tile URL
      let tilesUrl = config.source.tiles[0];

      if (isDynamic && options) {
        const params = buildQueryParams(options);
        if (config.popup?.band) params.push(`band=${config.popup.band}`);
        if (params.length > 0) tilesUrl = `${tilesUrl}?${params.join("&")}`;
      }

      // 2️⃣ Add or update source
      const existingSource = map.getSource(sourceId);
      if (!existingSource) {
        map.addSource(sourceId, { ...config.source, tiles: [tilesUrl] });
      } else {
        existingSource.setTiles([tilesUrl]);
      }

      // 3️⃣ Add or update layer
      if (!map.getLayer(layerId)) {
        const anchorLayer = options.priorityLayers?.find((id) =>
          map.getLayer(id),
        );

        map.addLayer(
          {
            id: layerId,
            source: sourceId,
            type: "raster",
            paint: { ...config.layer.paint, "raster-opacity": opacity },
          },
          anchorLayer || undefined,
        );

        if (options.priorityLayers) {
          options.priorityLayers.forEach((beforeLayerId) => {
            if (map.getLayer(beforeLayerId))
              map.moveLayer(layerId, beforeLayerId);
          });
        }
      } else {
        map.setLayoutProperty(layerId, "visibility", "visible");
        map.setPaintProperty(layerId, "raster-opacity", opacity);
      }

      // 4️⃣ Register fetch function with shared popup manager
      if (config.popup?.enabled) {
        const fetchFn = async (lng, lat) => {
          let url = `${config.popup.endpoint}?lon=${lng}&lat=${lat}`;

          if (isDynamic && options) {
            const params = buildQueryParams(options);
            if (params.length > 0) url += `&${params.join("&")}`;
          }

          const res = await fetch(url);
          if (!res.ok) return null;

          const data = await res.json();
          const value = data?.value;

          if (typeof value !== "number") return null;

          return {
            label: config.popup.label,
            val: value.toFixed(2),
            unit: config.popup.unit,
          };
        };

        sharedPopupManager.register(layerKey, fetchFn);
      }
    };

    if (show) {
      applyLayer();
    } else {
      // Hide layer
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "visibility", "none");
      }

      // Unregister from shared popup manager
      if (config.popup?.enabled) {
        sharedPopupManager.unregister(layerKey);
      }

      // Delayed cleanup
      cleanupTimeoutRef.current = setTimeout(() => {
        if (!isVisibleRef.current) {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
          if (map.getSource(sourceId)) map.removeSource(sourceId);
        }
      }, 5000);
    }

    // Re-apply layer after basemap style change
    map.on("style.load", applyLayer);

    return () => {
      if (cleanupTimeoutRef.current) clearTimeout(cleanupTimeoutRef.current);
      if (config.popup?.enabled) sharedPopupManager.unregister(layerKey);
      map.off("style.load", applyLayer);
    };
  }, [mapRef, layerKey, show, ...(isDynamic ? [JSON.stringify(options)] : [])]);

  return null;
};

export default useRasterTile;